<template>
  <ajax-table
    ref="objectGroupsTable"
    :resource="sortable ? 'object-groups/all' : 'object-groups'"
    :unpaged="sortable"
    :exportable="!sortable"
    :headers="[
      {text: 'objectGroup.name', value: 'name'},
      {text: 'objectGroup.description', value: 'description'},
      {text: 'objectGroup.commission', value: 'commission'},
      {text: 'objectGroup.commissionAdd', value: 'commissionFee'},
      {text: 'objectGroup.objectCount', value: 'objectCount'},
      {text: '', value: 'actions', sortable: false},
    ]"
    @drag-reordered="onDragReordered"
    @drag-started="onDragStarted"
    :filter="objectGroupsFilter"
  >
    <template v-slot:filters>
      <template v-if="sortable">
        <save-button @click="saveObjectGroupPriorities" :disabled="disableObjectGroupSortButtons" :label="trans('objectGroup.sortSave')"></save-button>
      </template>
      <template v-else>
        <v-col cols="6" md="6" lg="4">
          <v-select
            :label="trans('objectGroup.settlementOption')"
            v-model="objectGroupsFilterData.settlementOption"
            :items="objectGroupSettlementOptions"
            cols="4"
            class="pt-5"
            clearable
          ></v-select>
        </v-col>
        <v-col cols="6" md="6" lg="3">
          <search v-model="objectGroupsFilterData.search" width="auto" class="pt-0" />
        </v-col>
      </template>
    </template>

    <template v-slot:[tableSlotName]="{items}">
      <thead v-if="sortable">
        <tr>
          <th></th>
          <th>{{ trans('objectGroup.name') }}</th>
          <th>{{ trans('objectGroup.description') }}</th>
          <th>{{ trans('objectGroup.commission') }}</th>
          <th>{{ trans('objectGroup.commissionAdd') }}</th>
          <th>{{ trans('objectGroup.objectCount') }}</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <template v-for="objectGroup in items">
          <tr
            :key="'row-group-'+objectGroup.id"
            class="sortableRow"
          >
            <td v-show="sortable" class="px-1" style="width: 0.1%">
              <v-btn style="cursor: move" icon class="sortHandle"><v-icon>mdi-drag</v-icon></v-btn>
            </td>
            <td>
              <v-chip
                label color="transparent"
                @click="toggleObjectGroup(objectGroup)"
                style="cursor: pointer;"
                :title="trans('objectGroup.showObjectsInGroup', {group: objectGroup.name})"
                :disabled="loadingObjectsByGroup.get(objectGroup.id)"
              >
                <v-icon size="20" color="primary" left>
                  <template v-if="isObjectGroupToggled(objectGroup)">
                    mdi-chevron-down-box
                  </template>
                  <template v-else>
                    mdi-chevron-right-box
                  </template>
                </v-icon>
                {{ objectGroup.name }}
              </v-chip>
            </td>
            <td><div class="text--secondary">{{ objectGroup.description }}</div></td>
            <td>{{ objectGroup.commission }}%</td>
            <td>
              <currency-amount
                v-if="objectGroup.commissionFee !== 0"
                :amount="objectGroup.commissionFee"
                :currency="objectGroup.currency"
              ></currency-amount>
              <span v-else class="text--secondary">&ndash;</span>
            </td>
            <td>
              {{ objectGroup.objectCount }}
            </td>
            <td>
              <edit-button-icon @click.native="pushToRoute('ObjectGroupPage', {objectGroupId: objectGroup.id})" />
            </td>
          </tr>
          <template v-if="isObjectGroupToggled(objectGroup)">
            <tr
              v-for="object in objectsByGroup.get(objectGroup.id)"
              :key="'row-objects-' + objectGroup.id + '-' + object.id"
              class="row-object"
            >
              <td v-show="sortable"></td>
              <td class="pl-12"><v-icon class="mr-1" color="blue-grey lighten-1">mdi-home</v-icon>{{ object.name }}</td>
              <td>
                <div class="subtext">{{ object.street }}</div>
                <div class="subtext">{{ object.zipcode }} {{ object.city }} {{ object.country != 'Deutschland' ? (', ' + object.country) : '' }}</div>
              </td>
              <td>
                <div v-if="object.owner" class="subtext py-2">
                  <div>{{ object.owner.fullName }}</div>
                </div>
              </td>
              <td>
                <div v-if="object.owner" class="subtext py-2">
                  <div><a :href="'mailto:'+object.owner.email">{{ object.owner.email }}</a></div>
                  <div>{{ object.owner.mobile }}</div>
                </div>
              </td>
              <td></td>
              <td>
                <edit-button-icon @click.native="pushToRoute('ObjectPage', {objectId: object.id})" />
              </td>
            </tr>
          </template>
        </template>
      </tbody>
    </template>
  </ajax-table>
</template>

<script>
import AjaxTable from '../components/AjaxTable';
import {RouterUtils} from '../utils/router-utils';
import EditButtonIcon from '../components/EditIconButton';
import {LocaleUtils} from '../utils/locale-utils';
import CurrencyAmount from '../components/CurrencyAmount';
import SaveButton from '../components/SaveButton';
import {MainUtils} from '../utils/main-utils';
import {ObjectApi} from '@/api/object-api';
import Search from './Search';
import {FilterUtils} from '../utils/filter-utils';
import {objectGroupSettlementOptions} from '../lib/object-group-settlement-options';
import {comparison, eq} from 'rsql-builder';

export default {
  name: 'ObjectGroupsTable',
  mixins: [
    MainUtils,
    RouterUtils,
    LocaleUtils,
    FilterUtils
  ],
  components: {
    Search,
    EditButtonIcon,
    SaveButton,
    CurrencyAmount,
    AjaxTable
  },
  props: {
    sortable: {
      type: Boolean,
      default () {
        return false;
      }
    }
  },
  data: () => ({
    toggledObjectGroups: [],
    lastObjectGroups: [],
    disableObjectGroupSortButtons: false,
    /** @type {Map<number, ObjectDto[]>} */
    objectsByGroup: new Map(),
    /** @type {Map<number, boolean>} */
    loadingObjectsByGroup: new Map(),
    objectsWithoutGroupGiven: false,
    assignedObjectGroups: {},
    objectGroupsFilterData: {
      settlementOption: null,
      search: null
    },
    objectGroupSettlementOptions
  }),

  activated () {
    this.$refs.objectGroupsTable.loadData();
  },

  computed: {
    canAssignGroup() {
      return this.hasAnyPermission('OBJECT_GROUP_EDIT');
    },
    pageTitle () {
      return this.transPlural('objectGroup.objectGroup')
    },
    objectGroupsFilter () {
      let filters = [];
      if (this.objectGroupsFilterData.settlementOption) {
        filters.push(comparison('settlementOption', eq(this.objectGroupsFilterData.settlementOption)))
      }
      if (this.objectGroupsFilterData.search) {
        filters.push(this.buildSearchFilter(this.objectGroupsFilterData.search, ['id', 'name', 'description']))
      }
      // Only new arrivals filter is set as query param
      return filters;
    },

    tableSlotName () {
      return this.sortable ? 'custom' : 'body';
    }
  },

  methods: {
    toggleObjectGroup (objectGroup) {
      if (this.toggledObjectGroups.includes(objectGroup)) {
        this.toggledObjectGroups = this.toggledObjectGroups.filter(value => value !== objectGroup);
      } else {
        if (!this.objectsByGroup.has(objectGroup.id)) {
          this.loadingObjectsByGroup.set(objectGroup.id, true);
          this.$http.get('object-groups/' + objectGroup.id + '/objects')
            .then(response => {
              this.objectsByGroup.set(objectGroup.id, response.data.content);
              this.toggledObjectGroups.push(objectGroup);
              this.loadingObjectsByGroup.set(objectGroup.id, false);
            })
          ;
        } else {
          this.toggledObjectGroups.push(objectGroup);
        }
      }
    },

    isObjectGroupToggled (objectGroup) {
      return this.toggledObjectGroups.includes(objectGroup)
    },
    onDragReordered (eventData) {
      this.lastObjectGroups = eventData.items;
      this.toggledObjectGroups = [];
    },
    onDragStarted () {
      this.toggledObjectGroups = [];
    },
    saveObjectGroupPriorities () {
      let objectGroupPriorities = [];
      this.lastObjectGroups.forEach((objectGroup, i) => {
        objectGroupPriorities.push({
          id: objectGroup.id,
          priority: i + 1
        });
      });

      this.disableObjectGroupSortButtons = true;
      this.$http.post('object-groups/priorities', objectGroupPriorities)
        .then(response => {
          this.showSavedSuccessfullyNotification();
          this.stopObjectGroupSort();
        })
        .catch(reason => this.showErrorNotification())
        .finally(() => {
          this.disableObjectGroupSortButtons = false;
        })
    },
    stopObjectGroupSort () {
      this.disableObjectGroupSortButtons = true;
      this.$refs.objectGroupsTable.loadData()
        .finally(() => {
          this.disableObjectGroupSortButtons = false;
        });
    },
    assignObjectGroups() {
      ObjectApi.assignGroups(this.assignedObjectGroups)
        .then(_ => {
          this.$refs.objectWithoutGroupTable.loadData();
        })
    },
    resetSearchFilters () {
      this.objectGroupsFilterData.search = null;
    }
  }
}
</script>

<style>
.row-object {
  background-color: #F5F5F5;
}

.subtext {
  font-size: 0.9em;
  opacity: 0.8;
}
</style>
